import { useCallback, useState } from 'react';
import dayjs from 'dayjs';
import { unwrapResult } from '@reduxjs/toolkit';
import { useTranslation } from 'react-i18next';

import { createCompensation, submitCompensationAttachments, createManagementCompensation } from 'services';
import { REVERSE_DATE_FORMAT } from 'constants-module';
import { ApiManagementCompensation, CompensationFormValues } from 'components/common';
import { useAppDispatch, useNotification } from 'hooks';

import { triggerCreateAppEvent } from '../../GoogleAnalytics/event-triggers';

export const useSubmitRequest = (onSuccessSubmit: () => void) => {
  const dispatch = useAppDispatch();
  const [isLoading, setLoading] = useState(false);
  const sendNotification = useNotification();
  const { t } = useTranslation();

  const handleSubmit = useCallback(
    async ({ newAttachments, ...values }: CompensationFormValues) => {
      setLoading(true);

      const formattedValue = {
        ...values,
        billedOn: values.billedOn && dayjs(values.billedOn).startOf('day').format(REVERSE_DATE_FORMAT),
        targetPeriod: dayjs(values.targetPeriod).startOf('month').startOf('day').format(REVERSE_DATE_FORMAT),
      };

      try {
        let creationResult;

        if (formattedValue.employeeID) {
          creationResult = await dispatch(createManagementCompensation(formattedValue as ApiManagementCompensation));
        } else {
          creationResult = await dispatch(createCompensation(formattedValue));
        }

        const compensationRequestID = unwrapResult(creationResult)?.compensationRequestID;

        if (compensationRequestID && newAttachments?.length) {
          const attachmentResult = await dispatch(
            submitCompensationAttachments({
              compensationRequestID,
              files: newAttachments,
            }),
          );

          unwrapResult(attachmentResult);
        }

        setLoading(false);
        sendNotification(t('success'), 'success');
        onSuccessSubmit();
        triggerCreateAppEvent(formattedValue.type);
      } catch (e) {
        setLoading(false);
      }
    },
    [dispatch, sendNotification, t, onSuccessSubmit],
  );

  return { handleSubmit, isLoading };
};
